import './TaskDetails.scss'
import 'draft-js/dist/Draft.css'

import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import Icon from 'react-fa'
import ReactModal from 'react-modal'
import EditableTextField from '../EditableTextField'
import PostCommentPanel from '../PostCommentPanel'
import AssigneePanel from './AssigneePanel'
import CommentsPanel from './CommentsPanel'
import ChecklistPanel from '../ChecklistPanel'
import { ModalStyle } from '../../constants/modalConfig'
import Button from '@material-ui/core/Button'
import { Menu, MenuItem } from '@material-ui/core'
import CloseModalButton from '../CloseModalButton'
import classnames from 'classnames'
import { SHORT_DATE_FORMAT } from '../../constants/momentFormats'
import AddLinkDialog from '../AddLinkDialog'
import TaskLinksContainer from './TaskLinksContainer'
import moment from 'moment'
import 'moment-timezone'

export const DESCRIPTION_PLACEHOLDER_KEY = 'common.Label_Description'
export const DESCRIPTION_MAX_LENGTH = 4000
export const NAME_PLACEHOLDER_KEY = 'common.Label_Name'
export const NAME_MAX_LENGTH = 200

export const ICON_MENU_HEIGHT_OFFSET = -50

const menuButtonStyles = {
  padding: '0px',
  minWidth: '40px',
  maxWidth: '40px',
  marginLeft: '8px',
  borderRadius: '20px',
  lineHeight: '40px',
  height: '40px',
  verticalAlign: 'middle'
}

const menuPositioning = {
  anchorOrigin: { 'horizontal': 'right', 'vertical': 'top' },
  transformOrigin: { 'horizontal': 'right', 'vertical': ICON_MENU_HEIGHT_OFFSET }
}

const PlainTextField = (text) => <div className='task-modal__text-field'>{text}</div>

/**
 * @augments{ PureComponent<any, any> }
 */
class TaskDetails extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      menuOpen: false,
      linkModalOpen: false
    }
  }

  componentDidMount() {
    this.props.loadComments(this.props.task.id)
    this.props.loadTaskLinks(this.props.task.id)
    this.props.loadTaskChecklistItems(this.props.task.id)
  }

  handleMenuRequestClose = () => {
    this.setState({ menuOpen: false })
  }

  handleMenuTouchTap = (event) => {
    // This prevents ghost click.
    event.preventDefault()
    this.setState({ menuOpen: true })
  }

  openLinkModal = (event) => {
    // This prevents ghost click.
    event.preventDefault()
    this.setState({ linkModalOpen: true })
  }

  closeLinkModal = (event) => {
    this.setState({ linkModalOpen: false })
  }

  handleDeleteTask = (event) => {
    if (this.props.task.actions.delete) {
      this.setState({ menuOpen: false })
      this.props.onDelete(this.props.task.id, this.props.task.name)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.comments && prevProps.comments
      && prevProps.comments.length < this.props.comments.length) {
      requestAnimationFrame(() => {
        this.scrollRegion.scrollTop = this.scrollRegion.scrollHeight
      })
    }

    if (this.props.taskLinks && prevProps.taskLinks
      && prevProps.taskLinks.length < this.props.taskLinks.length) {
      requestAnimationFrame(() => {
        this.linksContainer.scrollIntoView(false)
      })
    }
  }

  render() {
    let { localize, task, taskLinks, taskChecklistItems } = this.props

    const linkButtonClasses = classnames({
      'task-modal__menu-button': true,
      'open': this.state.linkModalOpen
    })

    const checklistButtonClasses = classnames({
      'task-modal__menu-button': true
    })

    const menuButtonClasses = classnames({
      'task-modal__menu-button': true,
      'open': this.state.menuOpen
    })

    return (
      <ReactModal
        isOpen={this.props.modalOpen}
        style={{
          overlay: { ...ModalStyle.overlay },
          content: {
            ...ModalStyle.content,
            minHeight: '672px',
            width: 768,
            left: '0px'
          }
        }}
        onRequestClose={this.props.onClose}
        contentLabel={`Details for ${task.name}`}
        ariaHideApp={false}>
        {/*
          We have to introduce a flex container & wrapper due to issues with IE11 supporting min-height value
          see: https://github.com/philipwalton/flexbugs/tree/676abf342f9284c0fc616ee9c69006eb35a0b2d6#3-min-height-on-a-flex-container-wont-apply-to-its-flex-items
        */}
        <div className='FlexContainerWrapper'>
          <div className='FlexContainer'>
            <div className='task-modal__header'>
              <div className='task-modal__row header'>
                { task.actions.edit ? (
                  <EditableTextField
                    isRequired
                    onSave={this.props.saveName}
                    placeHolderText={localize(NAME_PLACEHOLDER_KEY)}
                    value={task.name}
                    maxLength={NAME_MAX_LENGTH}
                    disallowCr />
                ) : (
                  PlainTextField(task.name)
                )}
              </div>
              <CloseModalButton onClose={this.props.onClose} />
            </div>
            <div className='task-modal__row-parent'>
              <div className='task-modal__parent'>
                { task.parent &&
                  <div>
                    <span className='label'>{localize('uIResources.CustomView_Tasks_SF_ParentTask')}: </span>
                      {task.parent.name}
                  </div>
                }
              </div>
              <div className='task-modal__menu-button-container' ref='menu-button-container'>
                { task.actions.update &&
                  <Button
                    color='primary'
                    title={localize('taskBoards.Text_AddChecklistOrChecklistItem')}
                    disabled={!taskChecklistItems}
                    onClick={this.props.createTaskChecklistItem}
                    className={checklistButtonClasses}
                    style={menuButtonStyles}
                  >
                    <Icon name='list-ul' />
                  </Button>
                }
                { task.actions.edit &&
                  <Button
                    color='primary'
                    title={localize('taskBoards.Label_AttachTaskLink')}
                    disabled={!taskLinks}
                    onClick={this.openLinkModal}
                    className={linkButtonClasses}
                    style={menuButtonStyles}
                  >
                    <Icon name='paperclip' />
                  </Button>
                }
                <Button
                  color='primary'
                  onClick={this.handleMenuTouchTap}
                  className={menuButtonClasses}
                  style={menuButtonStyles}
                >
                  <Icon name='ellipsis-h' />
                </Button>
              </div>
              <Menu
                open={this.state.menuOpen}
                className='task-modal__menu'
                anchorEl={this.refs['menu-button-container']}
                anchorOrigin={menuPositioning.anchorOrigin}
                transformOrigin={menuPositioning.transformOrigin}
                onClose={this.handleMenuRequestClose}
              >
                <MenuItem
                  className='task-modal__menuitem'
                  onClick={this.handleDeleteTask}
                  disabled={!task.actions.delete}>
                  {localize('taskBoards.Text_DeleteTask')}
                </MenuItem>
              </Menu>
            </div>
            <div ref={(scrollRegion) => this.scrollRegion = scrollRegion} className='task-modal__scroll-region'>
              <div className='task-modal__body'>
                <div className='task-modal__row'>
                  <AssigneePanel
                    localize={localize}
                    task={task}
                    saveTaskAssignments={this.props.saveTaskAssignments} />
                  <div className='task-modal__planned-finish'>
                    <div className='task-modal__field'>{localize('tasks.Label_PlannedFinish')}</div>
                    <div className='task-modal__value'>
                      <Icon className='task-modal__calendar' name='calendar' />
                      { moment.utc(task.plannedFinish).format(SHORT_DATE_FORMAT) }
                    </div>
                  </div>
                </div>
                <div className='task-modal__row'>
                  <div className='task-modal__description'>
                    <div className='task-modal__value'>
                      { task.actions.edit ? (
                        <EditableTextField
                          onSave={this.props.saveDescription}
                          placeHolderText={localize(DESCRIPTION_PLACEHOLDER_KEY)}
                          value={task.description}
                          minRows={2}
                          maxLength={DESCRIPTION_MAX_LENGTH} />
                      ) : (
                        PlainTextField(task.description)
                      )}
                    </div>
                    <div ref={linksContainer => this.linksContainer = linksContainer}>
                      <TaskLinksContainer
                        taskLinks={taskLinks}
                        deleteTaskLink={this.props.deleteTaskLink}
                        allowDelete={!!task.actions.edit}
                        localize={localize}
                        />
                    </div>
                  </div>
                </div>
                <div className='task-modal__row'>
                  <ChecklistPanel
                    task={task}
                    items={this.props.taskChecklistItems}
                    createTaskChecklistItem={this.props.createTaskChecklistItem}
                    saveTaskChecklistItem={this.props.saveTaskChecklistItem}
                    deleteTaskChecklistItem={this.props.deleteTaskChecklistItem}
                    scrollContainer={this.scrollRegion}
                    lockAxis={'y'}
                  />
                </div>
                <div className='task-modal__row'>
                  <CommentsPanel
                    projectMembers={this.props.projectMembers}
                    comments={this.props.comments}
                    localize={localize}
                    localMoment={moment}
                    timeZone={this.props.timeZone}
                    deleteComment={this.props.deleteComment}
                  />
                </div>
              </div>
            </div>
            <div className='task-modal__footer'>
              <PostCommentPanel localize={localize} addComment={this.props.addComment} />
            </div>
          </div>
        </div>
        <AddLinkDialog
          open={this.state.linkModalOpen}
          closeModal={this.closeLinkModal}
          task={task}
          localize={localize} />
      </ReactModal>
    )
  }
}

TaskDetails.propTypes = {
  projectMembers: PropTypes.object.isRequired,
  addComment: PropTypes.func.isRequired,
  deleteComment: PropTypes.func.isRequired,
  loadComments: PropTypes.func,
  loadTaskLinks: PropTypes.func.isRequired,
  taskChecklistItems: PropTypes.array,
  createTaskChecklistItem: PropTypes.func.isRequired,
  saveTaskChecklistItem: PropTypes.func.isRequired,
  deleteTaskChecklistItem: PropTypes.func.isRequired,
  loadTaskChecklistItems: PropTypes.func.isRequired,
  comments: PropTypes.array,
  taskLinks: PropTypes.array,
  localize: PropTypes.func,
  modalOpen: PropTypes.bool,
  task: PropTypes.object,
  onClose: PropTypes.func,
  saveDescription: PropTypes.func.isRequired,
  saveName: PropTypes.func.isRequired,
  saveTaskAssignments: PropTypes.func.isRequired,
  timeZone: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired
}

export default TaskDetails



// WEBPACK FOOTER //
// ./src/components/TaskDetails/TaskDetails.js